import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import { makeStyles, alpha } from "@material-ui/core/styles";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  InputAdornment,
  Switch,
  Typography,
  Avatar,
} from "@material-ui/core";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import LogoLoader from "../LogoLoader";

const DURATIONS = [15, 30, 45, 60, 90, 120];
const AVATAR_COLORS = ["#2D7BE8", "#7b61ff", "#2e9d5b", "#f57c00", "#d81b60", "#00897b", "#5c6bc0", "#8d6e63"];
const avatarColor = (id) => AVATAR_COLORS[Math.abs(Number(id) || 0) % AVATAR_COLORS.length];
const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();

const formatDuration = (minutes) => {
  const m = Number(minutes) || 0;
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h}h${String(rest).padStart(2, "0")}` : `${h} h`;
};

// "150,00" / "1.250,5" / "150" -> 150 | 1250.5 | 150 (NaN se inválido)
const parsePrice = (text) => {
  const clean = String(text || "").trim().replace(/\./g, "").replace(",", ".");
  if (clean === "") return 0;
  return /^\d+(\.\d{0,2})?$/.test(clean) ? Number(clean) : NaN;
};

const priceToText = (value) =>
  Number(value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const useStyles = makeStyles((theme) => ({
  paper: { borderRadius: 16 },
  title: { paddingBottom: theme.spacing(1), "& h2": { fontWeight: 600 } },
  subtitle: { color: theme.palette.text.secondary, fontSize: 13, marginTop: 2 },
  section: {
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  hint: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 6, lineHeight: 1.45 },
  chips: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: {
    font: "inherit",
    fontSize: 13,
    fontWeight: 600,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    padding: "6px 12px",
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    cursor: "pointer",
    fontVariantNumeric: "tabular-nums",
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  chipOn: {
    borderColor: "transparent",
    backgroundColor: alpha(theme.palette.primary.main, 0.14),
    color: theme.palette.primary.main,
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  provider: {
    font: "inherit",
    fontSize: 13,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    padding: "4px 12px 4px 4px",
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    cursor: "pointer",
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  providerOn: {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    fontWeight: 600,
  },
  providerAvatar: { width: 26, height: 26, fontSize: 10, fontWeight: 700, color: "#fff" },
  providerAvatarOff: { filter: "grayscale(1)", opacity: 0.55 },
  warn: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    marginTop: theme.spacing(1.25),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    fontSize: 13,
    lineHeight: 1.45,
    backgroundColor: alpha(theme.palette.warning.main, theme.palette.type === "dark" ? 0.16 : 0.12),
    color: theme.palette.text.primary,
    "& svg": { color: theme.palette.warning.main, flexShrink: 0 },
  },
  switchRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 },
  preview: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 12,
    border: `1px dashed ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
    "& svg": { color: theme.palette.text.secondary, flexShrink: 0 },
  },
  previewOff: { color: theme.palette.warning.main, fontWeight: 600 },
  actions: { padding: theme.spacing(1.5, 3, 2) },
  button: { textTransform: "none", fontWeight: 600, borderRadius: 10 },
  btnWrapper: { position: "relative" },
  buttonProgress: { position: "absolute", top: "50%", left: "50%", marginTop: -12, marginLeft: -12 },
}));

const EMPTY = { name: "", durationMinutes: 30, priceText: "0,00", active: true, userIds: [] };

const ServiceModal = ({ open, onClose, serviceId, reload }) => {
  const classes = useStyles();

  const [form, setForm] = useState(EMPTY);
  const [customDuration, setCustomDuration] = useState(false);
  const [providers, setProviders] = useState([]);
  const [providersLoaded, setProvidersLoaded] = useState(false);
  const [loadingService, setLoadingService] = useState(false);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!open) return;
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list");
        const list = Array.isArray(data) ? data : data.users || [];
        // só faz sentido vincular profissionais habilitados
        setProviders(list.filter((u) => u.isServiceProvider));
        setProvidersLoaded(true);
      } catch (err) {
        setProvidersLoaded(false);
        toastError(err);
      }
    };
    fetchUsers();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setTouched(false);
    setStats(null);
    if (!serviceId) {
      setForm(EMPTY);
      setCustomDuration(false);
      return;
    }
    // Enquanto o GET não volta, os campos ficam bloqueados para não salvar
    // os valores padrão por cima do serviço.
    const fetchService = async () => {
      setLoadingService(true);
      try {
        const [{ data }, impact] = await Promise.all([
          api.get(`/services/${serviceId}`),
          api.get(`/services/${serviceId}/impact`).catch(() => null),
        ]);
        setForm({
          name: data.name || "",
          durationMinutes: data.durationMinutes || 30,
          priceText: priceToText(data.price),
          active: data.active !== false,
          userIds: (data.users || []).map((u) => u.id),
        });
        setCustomDuration(!DURATIONS.includes(Number(data.durationMinutes)));
        if (impact) setStats(impact.data);
      } catch (err) {
        toastError(err);
      }
      setLoadingService(false);
    };
    fetchService();
  }, [serviceId, open]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleProvider = (id) =>
    set("userIds", form.userIds.includes(id) ? form.userIds.filter((x) => x !== id) : [...form.userIds, id]);

  const name = form.name.trim();
  const duration = Number(form.durationMinutes);
  const price = parsePrice(form.priceText);
  const errors = {
    name: name.length < 2 ? "Informe um nome com pelo menos 2 letras." : name.length > 80 ? "Máximo de 80 caracteres." : "",
    duration:
      !Number.isInteger(duration) || duration < 5 || duration > 480 ? "Entre 5 e 480 minutos." : "",
    price: Number.isNaN(price) ? "Valor inválido. Ex.: 150,00" : "",
  };
  const hasErrors = Boolean(errors.name || errors.duration || errors.price);

  // Profissionais vinculados que não estão mais habilitados na agenda.
  const selectedProviders = providers.filter((p) => form.userIds.includes(p.id));
  const visibleOnPage = form.active && (providersLoaded ? selectedProviders.length > 0 : form.userIds.length > 0);

  const handleSave = async () => {
    setTouched(true);
    if (hasErrors) return;
    setSaving(true);
    const payload = {
      name,
      durationMinutes: duration,
      price,
      active: form.active,
      // mantém só profissionais ainda habilitados (se a lista não carregou,
      // não mexe nos vínculos)
      userIds: providersLoaded ? selectedProviders.map((p) => p.id) : form.userIds,
    };
    try {
      const { data } = serviceId
        ? await api.put(`/services/${serviceId}`, payload)
        : await api.post("/services", payload);
      toast.success(serviceId ? "Serviço salvo." : "Serviço criado.");
      if (typeof reload === "function") reload(data);
      onClose();
    } catch (err) {
      // não fecha: o usuário corrige e tenta de novo sem perder o que digitou
      toastError(err);
    }
    setSaving(false);
  };

  const subtitle = serviceId
    ? stats
      ? stats.pastAppointments || stats.futureAppointments
        ? `${stats.futureAppointments} agendamento${stats.futureAppointments === 1 ? "" : "s"} futuro${
            stats.futureAppointments === 1 ? "" : "s"
          } · ${stats.pastAppointments} no histórico`
        : "Ainda sem agendamentos"
      : " "
    : "Aparece na página pública quando tiver ao menos um profissional.";

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      classes={{ paper: classes.paper }}
    >
      {loadingService && <LogoLoader overlay />}
      <DialogTitle className={classes.title}>
        {serviceId ? "Editar serviço" : "Novo serviço"}
        <div className={classes.subtitle}>{subtitle}</div>
      </DialogTitle>
      <DialogContent dividers style={{ paddingTop: 0, paddingBottom: 0 }}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Serviço</div>
          <TextField
            id="service-name"
            label="Nome"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            onBlur={() => setTouched(true)}
            error={touched && Boolean(errors.name)}
            helperText={
              touched && errors.name
                ? errors.name
                : "É o nome que o cliente vê na página e nas mensagens do WhatsApp."
            }
            variant="outlined"
            size="small"
            fullWidth
            autoFocus={!serviceId}
            disabled={loadingService}
            inputProps={{ maxLength: 80 }}
          />
        </div>

        <div className={classes.section}>
          <div className={classes.sectionTitle}>Duração e preço</div>
          <div className={classes.chips} role="group" aria-label="Duração">
            {DURATIONS.map((d) => {
              const on = !customDuration && duration === d;
              return (
                <button
                  key={d}
                  type="button"
                  className={`${classes.chip} ${on ? classes.chipOn : ""}`}
                  aria-pressed={on}
                  disabled={loadingService}
                  onClick={() => {
                    setCustomDuration(false);
                    set("durationMinutes", d);
                  }}
                >
                  {formatDuration(d)}
                </button>
              );
            })}
            <button
              type="button"
              className={`${classes.chip} ${customDuration ? classes.chipOn : ""}`}
              aria-pressed={customDuration}
              disabled={loadingService}
              onClick={() => setCustomDuration(true)}
            >
              Outra
            </button>
          </div>
          <div className={classes.hint}>Define o tamanho de cada horário na agenda.</div>
          <div className={classes.grid2}>
            <TextField
              id="service-price"
            label="Preço"
              value={form.priceText}
              onChange={(e) => set("priceText", e.target.value.replace(/[^\d.,]/g, ""))}
              onBlur={() => !Number.isNaN(price) && set("priceText", priceToText(price))}
              error={Boolean(errors.price)}
              helperText={errors.price || "Deixe 0,00 para mostrar como grátis."}
              variant="outlined"
              size="small"
              disabled={loadingService}
              inputProps={{ inputMode: "decimal" }}
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
            />
            {customDuration && (
              <TextField
                id="service-duration"
            label="Duração em minutos"
                type="number"
                value={form.durationMinutes}
                onChange={(e) => set("durationMinutes", e.target.value)}
                error={Boolean(errors.duration)}
                helperText={errors.duration || " "}
                variant="outlined"
                size="small"
                disabled={loadingService}
                inputProps={{ min: 5, max: 480, step: 5 }}
              />
            )}
          </div>
        </div>

        <div className={classes.section}>
          <div className={classes.sectionTitle}>Quem atende</div>
          {providers.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              Nenhum usuário atende na agenda ainda. Ligue "Atende na agenda" em Usuários.
            </Typography>
          ) : (
            <div className={classes.chips} role="group" aria-label="Profissionais">
              {providers.map((p) => {
                const on = form.userIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`${classes.provider} ${on ? classes.providerOn : ""}`}
                    aria-pressed={on}
                    disabled={loadingService}
                    onClick={() => toggleProvider(p.id)}
                  >
                    <Avatar
                      className={`${classes.providerAvatar} ${on ? "" : classes.providerAvatarOff}`}
                      style={{ backgroundColor: avatarColor(p.id) }}
                    >
                      {initials(p.name)}
                    </Avatar>
                    {p.name}
                  </button>
                );
              })}
            </div>
          )}
          {providersLoaded && selectedProviders.length === 0 && (
            <div className={classes.warn}>
              <WarningRoundedIcon fontSize="small" />
              <span>
                Sem profissional, o serviço fica salvo, mas <strong>não aparece</strong> na página de agendamento.
              </span>
            </div>
          )}
          <div className={classes.hint}>Aparecem aqui só os usuários com "Atende na agenda" ligado.</div>
        </div>

        <div className={classes.section}>
          <div className={classes.switchRow}>
            <div>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                Disponível para agendamento
              </Typography>
              <div className={classes.hint} style={{ marginTop: 2 }}>
                Desligado: some da página pública e da IA. Os agendamentos já feitos continuam.
              </div>
            </div>
            <Switch
              checked={form.active}
              disabled={loadingService}
              onChange={(e) => set("active", e.target.checked)}
              color="primary"
              inputProps={{ "aria-label": "Disponível para agendamento" }}
            />
          </div>
          <div className={classes.preview}>
            <VisibilityOutlinedIcon fontSize="small" />
            <div style={{ minWidth: 0 }}>
              <div className={classes.hint} style={{ marginTop: 0 }}>
                Como o cliente vê
              </div>
              {visibleOnPage ? (
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {`${name || "Sem nome"} · ${formatDuration(duration)} · ${
                    price > 0 ? `R$ ${priceToText(price)}` : "Grátis"
                  }`}
                </Typography>
              ) : (
                <Typography variant="body2" className={classes.previewOff}>
                  {form.active ? "Não aparece na página (sem profissional)" : "Não aparece na página (desativado)"}
                </Typography>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose} disabled={saving} className={classes.button}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disableElevation
          disabled={saving || loadingService}
          className={`${classes.button} ${classes.btnWrapper}`}
        >
          {serviceId ? "Salvar alterações" : "Criar serviço"}
          {saving && <CircularProgress size={24} className={classes.buttonProgress} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceModal;
